import { Action } from '@reduxjs/toolkit';
import {
  BUSINESS_DETAILS_ROUTE,
  CONFIRMATION_ROUTE,
  COVER_DETAILS_BREAKDOWN_ROUTE,
  COVER_DETAILS_BUILDING_AND_CONTENTS_ROUTE,
  COVER_DETAILS_BUSINESS_INTERRUPTION_ROUTE,
  COVER_DETAILS_GLASS_ROUTE,
  COVER_DETAILS_LIABILITY_ROUTE,
  COVER_DETAILS_MONEY_ROUTE,
  COVER_DETAILS_PORTABLE_ITEMS_ROUTE,
  COVER_DETAILS_ROUTE,
  COVER_DETAILS_TAX_AUDIT_ROUTE,
  COVER_DETAILS_THEFT_ROUTE,
  EXTRA_INFO_CUSTOMER_HISTORY_ROUTE,
  EXTRA_INFO_MORE_BUSINESS_DETAILS_ROUTE,
  EXTRA_INFO_ROUTE,
  MEMBERSHIP_ROUTE,
  PAYMENTS_ROUTE,
  QUOTE_ROUTE,
  REVIEW_ROUTE,
  SELECT_COVER_ROUTE,
} from 'constants/routes';
import { useAppDispatch, useExtraInfo, usePayments, useSelectCover } from 'hooks';
import React, { ReactNode, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { toStep } from 'slices/step';
import { Cover } from 'types/cover';
import { Step } from 'types/enums';

import { selectIsBusinessDetailsValid } from '../../selectors/selectBusinessDetails';
import { isDeepEqual } from '../../utils';

const skipValidateStep = process.env.REACT_APP_SKIP_VALIDATE_STEP_ROUTE;

/** @description Check if the previous selected cover is complete. (Ignore Liability) */
const isPreviousCoverDetailsComplete = (
  currentCover: Cover,
  covers: Cover[],
  completedCovers: Cover[]
) =>
  currentCover === Cover.Liability
    ? false
    : completedCovers.includes(covers[covers.indexOf(currentCover) - 1]);

export const WithStepValidation = ({ children }: { children: ReactNode }) => {
  const { pathname, search } = useLocation();
  const dispatch = useAppDispatch();
  const businessDetailsValid = useSelector(selectIsBusinessDetailsValid);
  const { covers, completedCovers } = useSelectCover();
  const { isCustomerHistoryValid, isMoreBusinessDetailsValid } = useExtraInfo();
  const { isValid: _paymentsValid } = usePayments();

  const selectCoverValid = businessDetailsValid;
  const coverDetailsValid = selectCoverValid && isDeepEqual(covers, completedCovers);
  const quoteValid = coverDetailsValid;
  const extraInfoCustomerHistoryValid = quoteValid && isCustomerHistoryValid;
  const extraInfoMoreBusinessDetailsComplete =
    extraInfoCustomerHistoryValid && isMoreBusinessDetailsValid;
  const reviewValid = extraInfoMoreBusinessDetailsComplete;
  const paymentsValid = reviewValid && _paymentsValid;
  const { paymentState } = usePayments();

  const mapPath: Record<
    string,
    {
      isValid: boolean;
      action: Action;
    }
  > = {
    [BUSINESS_DETAILS_ROUTE]: {
      isValid: true,
      action: toStep(Step.BusinessDetails),
    },
    [SELECT_COVER_ROUTE]: {
      isValid: !!businessDetailsValid,
      action: toStep(Step.SelectCover),
    },
    [COVER_DETAILS_ROUTE]: {
      isValid: !!selectCoverValid,
      action: toStep(Step.CoverDetailsLiability),
    },
    [COVER_DETAILS_LIABILITY_ROUTE]: {
      isValid: !!selectCoverValid,
      action: toStep(Step.CoverDetailsLiability),
    },
    [COVER_DETAILS_BUILDING_AND_CONTENTS_ROUTE]: {
      isValid: isPreviousCoverDetailsComplete(Cover.BuildingAndContents, covers, completedCovers),
      action: toStep(Step.CoverDetailsBuildingAndContents),
    },
    [COVER_DETAILS_BUSINESS_INTERRUPTION_ROUTE]: {
      isValid: isPreviousCoverDetailsComplete(Cover.BuildingAndContents, covers, completedCovers),
      action: toStep(Step.CoverDetailsBuildingAndContents),
    },
    [COVER_DETAILS_PORTABLE_ITEMS_ROUTE]: {
      isValid: isPreviousCoverDetailsComplete(Cover.PortableItems, covers, completedCovers),
      action: toStep(Step.CoverDetailsPortableItems),
    },
    [COVER_DETAILS_THEFT_ROUTE]: {
      isValid: isPreviousCoverDetailsComplete(Cover.Theft, covers, completedCovers),
      action: toStep(Step.CoverDetailsTheft),
    },
    [COVER_DETAILS_MONEY_ROUTE]: {
      isValid: isPreviousCoverDetailsComplete(Cover.Money, covers, completedCovers),
      action: toStep(Step.CoverDetailsMoney),
    },
    [COVER_DETAILS_GLASS_ROUTE]: {
      isValid: isPreviousCoverDetailsComplete(Cover.Glass, covers, completedCovers),
      action: toStep(Step.CoverDetailsGlass),
    },
    [COVER_DETAILS_BREAKDOWN_ROUTE]: {
      isValid: isPreviousCoverDetailsComplete(Cover.Breakdown, covers, completedCovers),
      action: toStep(Step.CoverDetailsBreakdown),
    },
    [COVER_DETAILS_TAX_AUDIT_ROUTE]: {
      isValid: isPreviousCoverDetailsComplete(Cover.TaxAudit, covers, completedCovers),
      action: toStep(Step.CoverDetailsTaxAudit),
    },
    [MEMBERSHIP_ROUTE]: {
      isValid: !!coverDetailsValid,
      action: toStep(Step.Membership),
    },
    [QUOTE_ROUTE]: {
      isValid: !!coverDetailsValid,
      action: toStep(Step.Quote),
    },
    [EXTRA_INFO_ROUTE]: {
      isValid: !!quoteValid,
      action: toStep(Step.ExtraInfoCustomerHistory),
    },
    [EXTRA_INFO_CUSTOMER_HISTORY_ROUTE]: {
      isValid: !!quoteValid,
      action: toStep(Step.ExtraInfoCustomerHistory),
    },
    [EXTRA_INFO_MORE_BUSINESS_DETAILS_ROUTE]: {
      isValid: !!extraInfoCustomerHistoryValid,
      action: toStep(Step.ExtraInfoMoreBusinessDetails),
    },
    [REVIEW_ROUTE]: {
      isValid: !!extraInfoMoreBusinessDetailsComplete,
      action: toStep(Step.Review),
    },
    [PAYMENTS_ROUTE]: {
      isValid: !!reviewValid,
      action: toStep(Step.Payments),
    },
    [CONFIRMATION_ROUTE]: {
      isValid: !!paymentsValid,
      action: toStep(Step.Confirmation),
    },
  };

  const path = mapPath[pathname];

  useEffect(() => {
    if (path.isValid) dispatch(path.action);
  });

  if (paymentState === 'success' && pathname !== CONFIRMATION_ROUTE) {
    return <Navigate to={CONFIRMATION_ROUTE + search} replace />;
  }

  if (skipValidateStep || path.isValid) {
    return <> {children}</>;
  }
  return <Navigate to={BUSINESS_DETAILS_ROUTE + search} />;
};
